import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Layout from "../../components/layout/layout";
import { fetchArticleBySlug, updateArticleById } from "../../lib/articles";
import Markdown from "react-markdown";
import { supabase } from "../../lib/supabase";
import { Upload, X } from "lucide-react";
import toast from "react-hot-toast";

export default function EditArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [article, setArticle] = useState<any>(null);
  const [formData, setFormData] = useState({
    title: "",
    excerpt: "",
    content: "",
    image_url: "",
    author: "",
    category: "",
    published: false
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!slug) return;
      setIsLoading(true);
      try {
        const fetched = await fetchArticleBySlug(slug);
        if (fetched) {
          setArticle(fetched);
          setFormData({
            title: fetched.title || "",
            excerpt: fetched.excerpt || "",
            content: fetched.content || "",
            image_url: fetched.image_url || "",
            author: fetched.author || "",
            category: fetched.category || "",
            published: fetched.published || false
          });
        }
      } catch (error) {
        console.error("Error loading article:", error);
        toast.error("Erreur lors du chargement de l'article");
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [slug]);

  const handleSave = async () => {
    if (!article?.id || isSaving) return;
    
    setIsSaving(true);
    const toastId = toast.loading("Enregistrement en cours...");
    
    try {
      const updatedArticle = await updateArticleById(article.id, {
        ...formData,
        slug: slug // Ensure slug is included in update
      });
      
      if (updatedArticle) {
        toast.success("Article mis à jour avec succès", { id: toastId });
        navigate(`/ressources/articles/${slug}`);
      }
    } catch (error) {
      console.error("Error saving article:", error);
      toast.error("Erreur lors de la mise à jour", { id: toastId });
    } finally {
      setIsSaving(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const sanitizeFileName = (fileName: string) => {
    return fileName
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-zA-Z0-9.]/g, '-')
      .replace(/-+/g, '-')
      .toLowerCase();
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (!file || !file.type.startsWith('image/')) {
      toast.error('Le fichier doit être une image');
      return;
    }

    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error('L\'image ne doit pas dépasser 5MB');
      return;
    }

    const toastId = toast.loading('Upload en cours...');

    try {
      const sanitizedFileName = sanitizeFileName(file.name);
      const sanitizedSlug = encodeURIComponent(slug || '');
      const filePath = `articles/${sanitizedSlug}/${Date.now()}-${sanitizedFileName}`;
      
      const { error: uploadError } = await supabase.storage
        .from('articles')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('articles')
        .getPublicUrl(filePath);

      setFormData(prev => ({ ...prev, image_url: publicUrl }));
      toast.success('Image mise à jour', { id: toastId });
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Erreur lors de l\'upload', { id: toastId });
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Le fichier doit être une image');
      return;
    }

    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error('L\'image ne doit pas dépasser 5MB');
      return;
    }

    const toastId = toast.loading('Upload en cours...');

    try {
      const sanitizedFileName = sanitizeFileName(file.name);
      const sanitizedSlug = encodeURIComponent(slug || '');
      const filePath = `articles/${sanitizedSlug}/${Date.now()}-${sanitizedFileName}`;
      
      const { error: uploadError } = await supabase.storage
        .from('articles')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('articles')
        .getPublicUrl(filePath);

      setFormData(prev => ({ ...prev, image_url: publicUrl }));
      toast.success('Image mise à jour', { id: toastId });
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Erreur lors de l\'upload', { id: toastId });
    }
  };

  const removeImage = () => {
    setFormData(prev => ({ ...prev, image_url: '' }));
  };

  if (isLoading) {
    return <div className="p-10">Chargement…</div>;
  }

  const categories = [
    "Recrutement",
    "Marque employeur", 
    "RH & Management",
    "Onboarding",
    "Culture d'entreprise",
    "Innovation RH",
    "Général"
  ];

  return (
    <Layout title="Édition de l'article">
      <div className="container-custom max-w-5xl py-10">
        <h1 className="text-3xl font-bold mb-8">📝 Modifier l'article</h1>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="space-y-6">
            {/* Titre */}
            <div>
              <label className="block text-sm font-medium mb-2">Titre</label>
              <input
                type="text"
                name="title"
                className="w-full border border-neutral-200 rounded-lg px-4 py-3"
                value={formData.title}
                onChange={handleChange}
                placeholder="Titre de l'article"
              />
            </div>

            {/* Catégorie */}
            <div>
              <label className="block text-sm font-medium mb-2">Catégorie</label>
              <select
                name="category"
                className="w-full border border-neutral-200 rounded-lg px-4 py-3"
                value={formData.category}
                onChange={handleChange}
              >
                <option value="">Sélectionner une catégorie</option>
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            {/* Extrait */}
            <div>
              <label className="block text-sm font-medium mb-2">Extrait</label>
              <textarea
                name="excerpt"
                className="w-full border border-neutral-200 rounded-lg px-4 py-3 h-24"
                value={formData.excerpt}
                onChange={handleChange}
                placeholder="Bref résumé de l'article"
              />
            </div>

            {/* Image mise en avant */}
            <div>
              <label className="block text-sm font-medium mb-2">Image mise en avant</label>
              <div
                className={`border-2 border-dashed rounded-lg transition-colors ${
                  isDragging ? 'border-primary bg-primary/5' : 'border-neutral-200'
                }`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                {formData.image_url ? (
                  <div className="relative">
                    <img 
                      src={formData.image_url} 
                      alt="Preview" 
                      className="w-full h-48 object-cover rounded-lg"
                    />
                    <button
                      onClick={removeImage}
                      className="absolute top-2 right-2 p-1 bg-black/50 hover:bg-black/75 rounded-full text-white transition-colors"
                    >
                      <X size={20} />
                    </button>
                  </div>
                ) : (
                  <div className="p-8 text-center">
                    <Upload className="mx-auto h-12 w-12 text-neutral-400 mb-4" />
                    <p className="text-sm text-neutral-600 mb-2">
                      Glissez une image ici ou
                    </p>
                    <label className="inline-block px-4 py-2 bg-neutral-100 rounded-lg cursor-pointer hover:bg-neutral-200 transition-colors">
                      Parcourir
                      <input
                        type="file"
                        className="hidden"
                        accept="image/*"
                        onChange={handleFileSelect}
                      />
                    </label>
                  </div>
                )}
              </div>
            </div>

            {/* Auteur */}
            <div>
              <label className="block text-sm font-medium mb-2">Auteur</label>
              <input
                type="text"
                name="author"
                className="w-full border border-neutral-200 rounded-lg px-4 py-3"
                value={formData.author}
                onChange={handleChange}
                placeholder="Nom de l'auteur"
              />
            </div>

            {/* Boutons */}
            <div className="flex gap-4">
              <button
                className={`bg-gradient-to-r from-[#d03484] to-[#d47254] text-white px-6 py-3 rounded-lg font-medium hover:opacity-90 transition-opacity ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`}
                onClick={handleSave}
                disabled={isSaving}
              >
                {isSaving ? 'Enregistrement...' : '💾 Enregistrer'}
              </button>
              <button
                className="bg-neutral-100 text-neutral-700 px-6 py-3 rounded-lg font-medium hover:bg-neutral-200 transition-colors"
                onClick={() => navigate(`/ressources/articles/${slug}`)}
              >
                Annuler
              </button>
            </div>
          </div>

          {/* Contenu */}
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium mb-2">Contenu (Markdown)</label>
              <textarea
                name="content"
                className="w-full border border-neutral-200 rounded-lg px-4 py-3 h-[500px] font-mono"
                value={formData.content}
                onChange={handleChange}
                placeholder="Contenu de l'article en Markdown"
              />
            </div>

            <div>
              <h3 className="text-sm font-medium mb-2">Aperçu</h3>
              <div className="prose max-w-none bg-neutral-50 p-6 rounded-lg border border-neutral-200">
                <Markdown>{formData.content}</Markdown>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}